import React, {
  useCallback, useEffect, useId, useLayoutEffect, useRef,
} from "react"
import {
  Box, Flex, FormLabel, LayoutProps, TypographyProps, Textarea as ChakraTextArea, TextareaProps,
} from "@chakra-ui/react"
import Icon from "components/elements/Icon"
import { Control, useController } from "react-hook-form"

type Props = {
  defaultTranslation: string
  name: string
  control: Control
  label?: string
  labelWeight?: TypographyProps["fontWeight"]
  labelWidth?: LayoutProps["width"]
  disabled?: boolean
}

const LabelRedefinition = ({
  defaultTranslation, name, label, control, labelWeight = "bold", labelWidth = "25%", disabled,
}: Props) => {
  const { field } = useController({ name, control })
  const editableFieldId = useId()
  const defaultRef = useRef<HTMLTextAreaElement>(null)
  const editableRef = useRef<HTMLTextAreaElement | null>(null)

  // Both boxes fit the longer of the two texts, so they stay side by side at the same height
  const fitHeight = useCallback(() => {
    const boxes = [defaultRef.current, editableRef.current].filter((box) => box !== null)
    boxes.forEach((box) => box.style.setProperty("height", "auto"))
    const height = Math.max(...boxes.map((box) => (
      box.scrollHeight + box.offsetHeight - box.clientHeight
    )))
    boxes.forEach((box) => box.style.setProperty("height", `${height}px`))
  }, [])

  useLayoutEffect(fitHeight, [fitHeight, field.value, defaultTranslation])
  useEffect(() => {
    window.addEventListener("resize", fitHeight)

    return () => window.removeEventListener("resize", fitHeight)
  }, [fitHeight])

  return (
    <Flex direction="column">
      {label && (
        <FormLabel fontWeight={labelWeight} htmlFor={editableFieldId} w={labelWidth}>
          {label}
        </FormLabel>
      )}
      <Flex>
        <Box w="50%">
          <FittedTextArea ref={defaultRef} value={defaultTranslation} isDisabled />
        </Box>
        <Icon onClick={() => field.onChange(defaultTranslation)} icon="arrow-right" role="button" size={4} w="10%" mt={3} />
        <Box w="50%" mb={4}>
          <FittedTextArea
            {...field}
            ref={(element: HTMLTextAreaElement | null) => {
              field.ref(element)
              editableRef.current = element
            }}
            value={field.value ?? ""}
            id={editableFieldId}
            isDisabled={disabled}
            placeholder={defaultTranslation}
            bg="white"
          />
        </Box>
      </Flex>
    </Flex>
  )
}

const FittedTextArea = React.forwardRef<HTMLTextAreaElement, TextareaProps>((props, ref) => (
  <ChakraTextArea ref={ref} rows={1} minH={10} resize="none" overflow="hidden" {...props} />
))

export default LabelRedefinition
